// Centralized auth route guards with no imports from launch-routes to avoid circular deps.
import Vue from "vue";
import Router from "vue-router";
import { getStore } from "../common/runtime-context";
import { CONFIG_STORE_CONST, IConfigStoreState } from "../config/config-store";
import { ConfigData } from "../config/config-data";
import { handleResponseMessages } from "../common/handle-http-reponse";
import { messageDispatchHelper } from "../user-message/user-message-store";
import { USER_MESSAGE_LEVEL } from "../user-message/user-message-models";
import { AUTH_STORE_CONST } from "../auth/auth-store";
import { ICompetitionSummaryPublic } from "../competition/competition-models";
import { IBuilderOptions } from "../builder/builder-models";

// Generic guard logic (no dependency on route constants)
export function isLoggedInGuardGeneric(to: any, next: any, loginRoute: string) {
  console.log("[isLoggedInGuardGeneric] ENTER", { to: to.fullPath, loginRoute });
  
  // Set document title from route meta
  Vue.nextTick(() => {
    const title: unknown = to.meta && to.meta.title ? to.meta.title : null;
    if (title) {
      if (typeof title === "string") {
        document.title = title;
        return;
      }
      if (typeof title === "function") {
        document.title = title(to.params);
        return;
      }
    }
    document.title = "Entry4Sports";
  });

  let isUserLoggedIn = false;
  const store = getStore();

  if (!store) {
    // Store not initialized yet, allow navigation to continue
    console.log("[isLoggedInGuardGeneric] Store not initialized, allowing navigation");
    next();
    return;
  }

  const configStoreState: IConfigStoreState =
    store.state[CONFIG_STORE_CONST.CONFIG_CONST_MODULE_NAME];
  const userName = configStoreState?.configApp?.userInfo?.user?.display_name;

  if (userName === "" || userName === "ANONYMOUS") {
    console.log("[isLoggedInGuardGeneric] User is ANONYMOUS, fetching app config...");
    // Not initialized; fetch app config and then decide
    const prom = new ConfigData().getAppConfig();
    handleResponseMessages(prom);
    prom.then((response) => {
      console.log("[isLoggedInGuardGeneric] App config fetched", { errNo: response.errNo });
      if (response.errNo > 0) {
        messageDispatchHelper(
          response.error,
          USER_MESSAGE_LEVEL.ERROR.toString()
        );
        return;
      }
      store.commit(
        CONFIG_STORE_CONST.CONFIG_CONST_MODULE_NAME +
          "/" +
          CONFIG_STORE_CONST.CONFIG_MUTATIONS_SET_APP_CONFIG,
        response.data
      );
      const userNameData = response.data.userInfo.user.display_name;
      const isLoggedIn = userNameData.replace("ANONYMOUS", "").length > 0;
      store.commit(
        AUTH_STORE_CONST.AUTH_CONST_MODULE_NAME +
          "/" +
          AUTH_STORE_CONST.MUTATIONS_LOGGED_IN,
        isLoggedIn
      );
      console.log("[isLoggedInGuardGeneric] Calling next() after config fetch", { isLoggedIn });
      try {
        if (isLoggedIn) {
          next();
        } else {
          next({
            name: loginRoute,
            query: {
              redirectFrom: to.fullPath,
            },
          });
        }
      } catch (err) {
        if (!Router.isNavigationFailure(err)) {
          throw err;
        }
      }
    });
    return;
  } else {
    isUserLoggedIn = true;
  }

  if (!isUserLoggedIn) {
    console.log("[isLoggedInGuardGeneric] User not logged in, redirecting to login");
    next({
      name: loginRoute,
      query: {
        redirectFrom: to.fullPath,
      },
    });
    return;
  } else {
    console.log("[isLoggedInGuardGeneric] User is logged in, allowing navigation");
    next();
  }
}

// Factory to create a guard bound to a given login route name
export function makeLoginGuard(loginRouteName: string) {
  return (to: any, _from: any, next: any) => {
    console.log("[makeLoginGuard] ENTER", { to: to.fullPath, loginRouteName });
    return isLoggedInGuardGeneric(to, next, loginRouteName);
  };
}

// Conditional guard that checks IBuilderOptions simpleRegistration.enabled && simpleRegistration.ui.showDobYearOnly
// If both are true, allows access without authentication. Otherwise, requires authentication.
export function conditionalDobYearGuard(competition: ICompetitionSummaryPublic, loginRouteName: string) {
  return (to: any, _from: any, next: any) => {
    // Check if simple registration is enabled and year-only DOB is configured
    const simpleRegEnabled = competition?.options?.simpleRegistration?.enabled || false;
    const showDobYearOnly = competition?.options?.simpleRegistration?.ui?.showDobYearOnly || false;

    if (simpleRegEnabled && showDobYearOnly) {
      // Year-only DOB mode is enabled, allow access without authentication
      next();
      return;
    }

    // Year-only DOB mode not enabled, check authentication
    let isUserLoggedIn = false;
    const store = getStore();

    if (!store) {
      // Store not initialized yet, redirect to login
      next({
        name: loginRouteName,
        query: {
          redirectFrom: to.fullPath,
        },
      });
      return;
    }

    const configStoreState: IConfigStoreState =
      store.state[CONFIG_STORE_CONST.CONFIG_CONST_MODULE_NAME];
    const userName = configStoreState?.configApp?.userInfo?.user?.display_name;

    if (userName === "" || userName === "ANONYMOUS") {
      // Not initialized; fetch app config and then decide
      const prom = new ConfigData().getAppConfig();
      handleResponseMessages(prom);
      prom.then((response) => {
        if (response.errNo > 0) {
          messageDispatchHelper(
            response.error,
            USER_MESSAGE_LEVEL.ERROR.toString()
          );
          return;
        }
        store.commit(
          CONFIG_STORE_CONST.CONFIG_CONST_MODULE_NAME +
            "/" +
            CONFIG_STORE_CONST.CONFIG_MUTATIONS_SET_APP_CONFIG,
          response.data
        );
        const userNameData = response.data.userInfo.user.display_name;
        const isLoggedIn = userNameData.replace("ANONYMOUS", "").length > 0;
        store.commit(
          AUTH_STORE_CONST.AUTH_CONST_MODULE_NAME +
            "/" +
            AUTH_STORE_CONST.MUTATIONS_LOGGED_IN,
          isLoggedIn
        );
        try {
          if (isLoggedIn) {
            next();
          } else {
            next({
              name: loginRouteName,
              query: {
                redirectFrom: to.fullPath,
              },
            });
          }
        } catch (err) {
          if (!Router.isNavigationFailure(err)) {
            throw err;
          }
        }
      });
      return;
    } else {
      isUserLoggedIn = true;
    }

    if (!isUserLoggedIn) {
      next({
        name: loginRouteName,
        query: {
          redirectFrom: to.fullPath,
        },
      });
      return;
    } else {
      next();
    }
  };
}

//  create a condtional guard, that takes an ICompetitionSummaryPublic and checks
// IBuilderOptions simpleRegistration.enabled && simpleRegistration.ui.showDobYearOnly
// If yes, let the user continue.
